<template>
  <div id="messages" class="relative">
    <MessagesLayout :selectedChannelUrl="selectedChannelUrl" :showNotFound="queryParamChannelNotFound && !selectedChannelUrl">
      <!-- Channel list for both mobile and desktop -->
      <template #channelList>
        <ChannelList
          :channels="channels"
          :selectedChannelUrl="selectedChannelUrl"
          :loading="loadingChannels"
          :emptyTitle="emptyStateConfig.title"
          :emptyMessage="emptyStateConfig.message"
          :emptyActionText="emptyStateConfig.actionText || ''"
          :emptyActionUrl="emptyStateConfig.actionUrl || ''"
          :emptyActionTo="emptyStateConfig.actionTo || null"
          @select="handleSelectChannel"
        />
      </template>

      <!-- Channel list error (shown above the list) -->
      <template #channelListHeader>
        <div v-if="channelListError" class="p-16 border-b border-red-300 bg-red-50">
          <p class="text-caption text-red-700 mb-8">{{ channelListError }}</p>
          <button class="text-s font-medium text-red-700 hover:text-red-900" @click="fetchChannelList">Retry</button>
        </div>
      </template>

      <!-- Conversation header -->
      <template #header>
        <ChannelHeader
          :title="headerTitle"
          :subtitle="''"
          :avatarName="headerAvatarName"
          :avatarUrl="headerAvatarUrl"
          :showBack="true"
          :currentChannelUrl="selectedChannelUrl"
          :otherUserInformation="selectedOtherUser"
          @back="handleSelectChannel('')"
        />
      </template>

      <!-- Message list -->
      <template #messageList>
        <MessageList
          ref="messageList"
          :messages="displayMessages"
          :loading="loadingMessages"
          :hideEmptyState="hideMessageListEmptyState"
          emptyTitle="No messages yet"
          emptyMessage="Start the conversation when you are ready."
        />
      </template>

      <!-- Message load error -->
      <template #messageError>
        <div v-if="messageLoadError" class="px-16 py-8 bg-red-50 border-t border-red-300">
          <p class="text-caption text-red-700 mb-8">{{ messageLoadError }}</p>
          <button class="text-s font-medium text-red-700 hover:text-red-900" @click="retryLoadMessages">Retry</button>
        </div>
      </template>

      <!-- Send error -->
      <template #sendError>
        <p v-if="sendError" class="text-caption text-red-600 px-16 pb-8">{{ sendError }}</p>
      </template>

      <!-- Message composer -->
      <template #composer>
        <MessageComposer
          v-if="showComposer"
          :value="draftMessage"
          :loading="sending"
          @input="draftMessage = $event"
          @send="handleSend(draftMessage)"
        />
      </template>

      <!-- Not found state -->
      <template #notFound>
        <ConversationNotFound @back="() => handleSelectChannel('')" />
      </template>

      <!-- Empty state when no channel selected -->
      <template #empty>
        <EmptyStateCard
          :title="emptyStateConfig.title"
          :message="emptyStateConfig.message"
          :actionText="emptyStateConfig.actionText"
          :actionUrl="emptyStateConfig.actionUrl"
          :actionTo="emptyStateConfig.actionTo"
          @action="$emit('action')"
        />
      </template>
    </MessagesLayout>
    <UpgradePrompt v-if="isBasicParent && channels.length > 0" />
  </div>
</template>

<script lang="ts">
import { mapState, mapActions, mapGetters } from 'vuex';
import MessagesLayout from '@/components/messages/MessagesLayout.vue';
import EmptyStateCard from '@/components/empty_state_card/EmptyStateCard.vue';
import ChannelList from '@/apps/messages/components/channelList/ChannelList.vue';
import ChannelHeader from '@/apps/messages/components/channelHeader/ChannelHeader.vue';
import ConversationNotFound from '@/apps/messages/components/emptyState/ConversationNotFound.vue';
import MessageList from '@/apps/messages/components/messageList/MessageList.vue';
import MessageComposer from '@/apps/messages/components/messageList/MessageComposer.vue';
import UpgradePrompt from '@/apps/messages/components/UpgradePrompt.vue';
import selectQueryParamChannel from '@/apps/messages/helpers/channelSelection';
import { sendSegmentEvent } from '@/lib/segment';
import type { EmptyStateConfig } from '@/apps/messages/types';

const EMPTY_STATE_CONFIGS: Record<'parent' | 'sitter', EmptyStateConfig> = {
  parent: {
    title: 'No messages',
    message: 'Your conversations with sitters will appear here',
    actionText: 'Search sitters',
    actionUrl: '/availability/search',
  },
  sitter: {
    title: 'No messages',
    message:
      "Your conversations with families will appear here after they reach out first or you've applied to their job opening",
    actionText: 'Apply to jobs',
    actionUrl: '/provider/jobs/search',
  },
};

export default {
  name: 'MessagesView',
  components: {
    MessagesLayout,
    EmptyStateCard,
    ChannelList,
    ChannelHeader,
    ConversationNotFound,
    MessageList,
    MessageComposer,
    UpgradePrompt,
  },
  data() {
    return {
      draftMessage: '',
    };
  },
  computed: {
    ...mapGetters('account', {
      account: 'getAccount',
      accountType: 'accountType',
    }),
    accountId(): string {
      return this.account?.id || '';
    },
    ...mapState('messages', {
      channels: 'channels',
      selectedChannelUrl: 'currentChannelUrl',
      loadingChannels: 'loadingChannels',
      channelListError: 'channelListError',
      messages: 'messages',
      loadingMessages: 'loadingMessages',
      messageLoadError: 'messageLoadError',
      selectedOtherUser: 'selectedOtherUser',
      selectedChannelMembers: 'selectedChannelMembers',
      sendError: 'sendError',
      sending: 'sending',
      queryParamChannelNotFound: 'queryParamChannelNotFound',
      isMessagingConnected: 'isConnected',
      lastReceivedMessageId: 'lastReceivedMessageId',
      lastReceivedChannelUrl: 'lastReceivedChannelUrl',
      rawChannels: 'rawChannels',
    }),
    displayMessages(): any[] {
      return this.selectedOtherUser?.showMessageHistory === false ? [] : this.messages;
    },
    showComposer(): boolean {
      return Boolean(this.selectedChannelUrl) && !this.selectedOtherUser?.showTrustSafetyBanner;
    },
    headerTitle(): string {
      return this.selectedOtherUser?.name || (this.selectedChannelUrl ? 'Conversation' : 'Messages');
    },
    headerAvatarName(): string {
      return this.selectedOtherUser?.name || '';
    },
    headerAvatarUrl(): string {
      return this.selectedOtherUser?.image || '';
    },
    emptyStateConfig(): EmptyStateConfig {
      return EMPTY_STATE_CONFIGS[this.accountType];
    },
    hideMessageListEmptyState(): boolean {
      return Boolean(this.selectedOtherUser?.showTrustSafetyBanner);
    },
    isBasicParent(): boolean {
      return Boolean(this.account?.isBasic?.() && this.account?.isParent?.());
    },
  },
  watch: {
    // Refreshes messages for the currently viewed channel when new message is received in that channel
    lastReceivedMessageId() {
      if (this.lastReceivedChannelUrl === this.selectedChannelUrl && this.selectedChannelUrl) {
        this.loadMessagesForChannel(this.selectedChannelUrl);
      }
    },
  },
  created() {
    this.initializeMessaging();
  },
  beforeDestroy() {
    this.disconnectMessaging();
  },
  methods: {
    ...mapActions('messages', {
      fetchChannelList: 'fetchChannelList',
      selectChannel: 'selectChannel',
      loadMessagesForChannel: 'loadMessagesForChannel',
      sendMessage: 'sendMessage',
      disconnectMessaging: 'disconnectMessaging',
    }),
    async initializeMessaging() {
      await this.$store.dispatch('messages/initializeMessaging');
      if (!this.isMessagingConnected) {
        this.$store.commit('messages/setLoadingMessages', false);
        return;
      }

      // Always fetch fresh channels when component is created
      await this.fetchChannelList();

      // If user is coming from external link with UUID, select that channel
      if (this.$route.query.uuid) {
        this.syncSelectionFromRoute();
        return;
      }

      if (this.selectedChannelUrl) {
        await this.loadMessagesForChannel(this.selectedChannelUrl);
        return;
      }

      this.syncSelectionFromRoute();
    },

    syncSelectionFromRoute() {
      const { uuid: uuidParam } = this.$route.query as { uuid?: string };
      if (uuidParam && this.rawChannels.length) {
        selectQueryParamChannel({
          uuidParam,
          allChannels: this.rawChannels,
          updateCurrentChannelUrl: (url: string) => this.handleSelectChannel(url),
          updateCurrentChannelMembers: () => {},
          changeToChannelPanel: () => {},
          updateChannelNotFound: () => {
            this.$store.commit('messages/setQueryParamChannelNotFound', true);
          },
          sendSegmentEvent,
        });
        return;
      }

      // Select first channel only on desktop
      // Channel list should be full screen on mobile when user navigates to messages
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile && this.rawChannels.length > 0) {
        this.handleSelectChannel(this.rawChannels[0].url);
      }
    },

    async handleSelectChannel(channelUrl: string) {
      if (!channelUrl) {
        this.$store.commit('messages/selectChannel', '');
        return;
      }

      await this.selectChannel(channelUrl);
    },

    async handleSend(message: string) {
      if (!this.isMessagingConnected || !this.selectedChannelUrl) return;
      if (!message?.trim()) return;
      this.draftMessage = '';
      await this.sendMessage(message);
    },

    retryLoadMessages() {
      if (this.selectedChannelUrl) {
        this.loadMessagesForChannel(this.selectedChannelUrl);
      }
    },
  },
};
</script>

<style lang="scss">
main:has(#messages) ~ #onetrust-consent-sdk #ot-sdk-btn-floating {
  display: none;
}

// hide zendesk support icon on messages page, approved by CS
main:has(#messages) ~ div:has(iframe#launcher) {
  display: none !important;
}
</style>
